import type { PhoneNumber } from '@/types/crm';

import { formatPhoneNumber } from './phone';

/**
 * Format a multi-value array as a string for display in limited-space contexts.
 *
 * Uses the "+N more" pattern for overflow items, consistent with PropertyValueLabel.
 *
 * @example
 * formatMultiValue(['a@x.com', 'b@x.com', 'c@x.com']) // "a@x.com +2 more"
 * formatMultiValue(['a@x.com']) // "a@x.com"
 * formatMultiValue([]) // ""
 * formatMultiValue(null) // ""
 */
export function formatMultiValue(
  values: unknown[] | null | undefined,
  maxVisible: number = 1
): string {
  if (!values || !Array.isArray(values) || values.length === 0) {
    return '';
  }

  // Filter out null/undefined values
  const validValues = values.filter(v => v !== null && v !== undefined);
  if (validValues.length === 0) {
    return '';
  }

  // Format individual value to string
  const formatValue = (v: unknown): string => {
    if (typeof v === 'string') {
      return v;
    }
    if (typeof v === 'number') {
      return String(v);
    }
    if (typeof v === 'object' && v !== null) {
      // Handle phone number objects. Delegate to the canonical formatter
      // (libphonenumber-js) so the calling code is resolved correctly
      // ("+1 415 …") — the previous inline concat used the ISO country_code
      // ("US") and produced "+US 415…" everywhere this helper formats phones.
      if ('country_code' in v && 'national_number' in v) {
        return formatPhoneNumber(v as PhoneNumber);
      }
      return JSON.stringify(v);
    }
    return String(v);
  };

  const visibleValues = validValues.slice(0, maxVisible).map(formatValue);
  const overflowCount = validValues.length - maxVisible;

  if (overflowCount > 0) {
    return `${visibleValues.join(', ')} +${overflowCount} more`;
  }

  return visibleValues.join(', ');
}

/**
 * Format a value that may be a single value or an array.
 * For arrays, uses the "+N more" pattern.
 * For single values, returns the value as a string.
 */
export function formatMaybeMultiValue(
  value: unknown,
  maxVisible: number = 1
): string {
  if (Array.isArray(value)) {
    return formatMultiValue(value, maxVisible);
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
}
